import { type ShouldBlockFn, useBlocker } from '@tanstack/react-router';
import { Trans, useTranslation } from 'react-i18next';
import { isEmpty } from 'remeda';

import { shouldPrompt } from '@@containers/LeavePrompt/utils';
import Modal from '@@containers/Modal/Modal';
import { ModalCloseEventSource } from '@@containers/Modal/types';
import { useLeavePromptStore } from '@@scripts/store/useLeavePromptStore';

type BlockerLocation = Parameters<ShouldBlockFn>[0]['current'];

const getPromptedEntries = (current: BlockerLocation, next: BlockerLocation) =>
    Object.values(useLeavePromptStore.getState().entries).filter((entry) =>
        shouldPrompt(entry, current, next),
    );

const shouldBlock: ShouldBlockFn = ({ current, next }) =>
    getPromptedEntries(current, next).length > 0;

const hasEntries = () => !isEmpty(useLeavePromptStore.getState().entries);

/**
 * Asks before a navigation leaves unsaved changes, and before a reload or closing the tab while there
 * are any. One for the app, since TanStack asks every blocker in turn: one per form would ask once per
 * dirty form.
 */
const NavPrompt = () => {
    const { t } = useTranslation();
    const blocker = useBlocker({
        shouldBlockFn: shouldBlock,
        enableBeforeUnload: hasEntries,
        withResolver: true,
    });

    if (blocker.status !== 'blocked') {
        return null;
    }

    const entries = getPromptedEntries(blocker.current, blocker.next);
    // t('router.modal.content')
    const messageKey =
        entries.find((entry) => entry.messageKey)?.messageKey ?? 'router.modal.content';

    const handleLeave = () => {
        entries.forEach((entry) => entry.onLeave?.());
        blocker.proceed();
    };

    const handleStay = () => blocker.reset();

    // The buttons are reversed: cancel leaves, confirm stays
    const handleClose = (closeEventSource?: ModalCloseEventSource) => {
        if (closeEventSource === ModalCloseEventSource.CANCEL_BUTTON) {
            handleLeave();
        } else {
            handleStay();
        }
    };

    return (
        <Modal
            type="confirm"
            reverseButtonOrder
            title={t('router.modal.title')}
            cancelButtonText={t('router.modal.leave')}
            confirmButtonText={t('router.modal.stay')}
            onClose={handleClose}
            onConfirm={handleStay}
        >
            <Trans i18nKey={messageKey} />
        </Modal>
    );
};

export default NavPrompt;
